const moment = require("moment");
const _ = require("lodash");
const StringUtil = require("../../../shared/StringUtil");
const GradeChapter = require("../../../shared/GradeChapter");
const TrainerCore = require("../../../shared/TrainerCore");
const ElevShared = require("../../../shared/ElevShared");
const Constants = require("../../../shared/Constants");
const LabUtil = require("../../../shared/LabUtil");
const UnlockUtil = require("./UnlockUtil");
const OpsBlock = require("../vault/OpsBlock");
const OpsLab = require("../../lab/vault/OpsLab");
const OpsUser = require("../vault/OpsUser");
const {
	submitResponse,
	giveUpResponse,
	repeatResponse,
	submitTooSoonResponse,
} = require("../../../shared/data/messages.json");

//
// General Copy
// Note: This file cannot contain any jsx. Anything that needs jsx should move to its own component in /components.
// TODO: We may want to split this up more by bundle rather than having one huge file of copy.
//

// Used in the ErrorMessage.jsx view, often rendered when things fail to load.
const standardErrorMessages = {
	userload:
		"You may have been signed out. Click one of the buttons below to try again or sign out.",
	userdatabase:
		"Uh oh! We're having trouble loading the information for this page. Click the button below to try again.",
	refresh:
		"Uh oh! Something failed to load properly. Click the button below to try again.",
	refreshOffice:
		"Something failed to load properly. Click the button below to try again.",
	database:
		"Uh oh! Our server is experiencing difficulties. Please wait a bit, then click the button below to try again.",
	connection:
		"Uh oh! Something failed to load properly. Check your internet connection and click the button below to try again.",
	"404": "Oh no! The page you requested cannot be found.",
	"404Office":
		"Oh no! The page you requested cannot be found. Click here to go to the Beast Academy home page.",
	playblock:
		"Uh oh! Something went wrong with this lesson. We're on it! Go back to the chapter page to choose another lesson.",
	playtrophy:
		"Uh oh! Something went wrong with this trophy problem. We're on it! Go back to the chapter page and choose another trophy problem.",
	playhistory:
		"Uh oh! Something went wrong with this lesson's history. Go back to the chapter page and choose another lesson.",
	noproblems:
		"Uh oh! This lesson is missing problems. Go back to the chapter page and choose another lesson.",
};
const forStandardError = function(key) {
	return standardErrorMessages[key];
};

// Used by functions in this file to display message for errors that can happen
// anywhere.
const commonErrorTypes = ElevShared.commonErrorTypes;

const get404Copy = function() {
	return {
		message: "Oh no! The page you requested cannot be found.",
		home: "Click here to go to the Beast Academy home page.",
	};
};

const getProtectedViewCopy = function(user) {
	let signedInMessage = "";
	if (user && user.model && (user.model.email || user.model.displayName)) {
		const displayName = user.model.email || user.model.displayName;
		signedInMessage = ``;
		if (OpsUser.isStudentOnly(user)) {
			signedInMessage += `You are signed in as ${displayName}, which is a student account. Your account does not have access to view this page.`;
		} else {
			signedInMessage += `Your account doesn't have access to this page. Visit your Accounts page to manage subscriptions, or contact support if you're not sure what to do next.`;
		}
	}

	return {
		notLoggedIn: `You must be signed in to view this page.`,
		badPermissions: signedInMessage,
		iplBlocked: `Your account has been moved.`,
	};
};

// This one is displayed in a small box, not a modal, so keep it short.
// This must return falsy if nothing is failing.
const forGlobalErrorNotice = function({
	hasResultError = false,
	disconnected = false,
} = {}) {
	if (disconnected) {
		return `Unable to connect. Please check your internet connection and firewall settings, or update your web browser.`;
	} else if (hasResultError) {
		return `Your recent progress did not save. Resending...`;
	}
	return null;
};

// Used by GlobalModal.jsx and the showMessage vault custom event.
const forErrorMessageType = function(messageType, data) {
	let message;
	const refreshSuffix =
		data && data.refreshOnClose ? ` Please refresh the page.` : "";
	const bestBrowsers = "Google Chrome, Mozilla Firefox, or Apple Safari";

	if (messageType === "disconnected") {
		message =
			`Your connection to our server has been lost. ` +
			`Please check your internet connection, and try again.`;
	} else if (messageType === "tooManyRequests") {
		message =
			`You have made too many requests to the server. ` +
			`Please wait several minutes, then refresh and try again.`;
	} else if (messageType === "wrongVersion") {
		message =
			"Beast Academy has been updated! Please refresh to see the latest version.";
	} else if (messageType === "bookTestScheduled") {
		message = `You have an exam scheduled today. Please click "HW" on the top menu to access that exam. This chapter test will unlock later.`;
	} else if (messageType === "browserNotRecommended") {
		message =
			`Beast Academy Online works best on ${bestBrowsers}. ` +
			`Please switch to one of those browsers for the optimal playing experience.`;
	} else if (messageType === "browserNotRecommendedAndOutdated") {
		message =
			`Beast Academy Online works best on ${bestBrowsers}. ` +
			`Please switch to one of those browsers or update to version ${data.requiredVersion} of ${data.browser} to play.`;
	} else if (messageType === "browserNotSupported") {
		message =
			`Beast Academy Online works best on ${bestBrowsers}. ` +
			`Please switch to one of those browsers in order to play.`;
	} else if (messageType === "browserOutdated") {
		message =
			`You are on version ${data.version} of ${data.browser}. ` +
			`BA Online works best on version ${data.requiredVersion} or later. Please update your browser.`;
	} else if (messageType === "browserMacChromeVersionIssue") {
		message =
			`You are currently using Chrome on Mac OS ${data.osVersion}. ` +
			`BA Online works best on newer Mac OS or using Apple Safari or Mozilla Firefox. ` +
			`Please update your OS or switch browsers to play.`;
	} else if (messageType === "cookieWarning") {
		message =
			`You must have cookies enabled to sign in to BA Online. ` +
			`Please enable cookies and refresh to continue.`;
	} else if (messageType === "nullContext") {
		message =
			`Your browser experienced an error with drawing graphics. ` +
			`Please refresh the page.`;
	} else if (messageType === "resultFailed") {
		const blockNamePhrase = data.blockName ? ` in ${data.blockName}` : "";
		// When a block gets marked under construction due to server trouble.
		message =
			`Something is wrong with the problems you worked on ${blockNamePhrase}. ` +
			`Our staff has been notified, and we will fix it soon. ` +
			`You might need to wait a few days before trying this lesson again.`;
	} else if (messageType === "resultRefresh") {
		message =
			`You have entered the classroom in a different tab or window.` +
			refreshSuffix;
	} else if (messageType === "failedBugReport") {
		if (data && data.errorCode === "E_NO_DESCRIPTION") {
			message = "Bug reports require a description.";
		} else {
			message =
				"Your bug report failed to send. Please let your parent or teacher know or contact tech-support@beastacademy.com for further assistance.";
		}
	} else if (messageType === "previousResultForBrokenSetDeleted") {
		const groupName = data.isTrophy ? "this trophy problem" : "these problems";
		const newName = data.isTrophy ? "trophy problem" : "set of problems";
		message = `Oh no! There were some issues with your previous work on ${groupName}. Here is a new ${newName} to start on.`;
	} else if (messageType === "previousResultForOutdatedTrophyDeleted") {
		message = `Oh no! There were some issues with this trophy problem. Here is a new trophy problem to start on.`;
	} else if (messageType === "currentResultForBrokenSetDeleted") {
		const currentName = data.isTrophy ? "the trophy problem" : "the problems";
		const newName = data.isTrophy ? "trophy problem" : "set of problems";
		message = `Oh no! There were some issues with ${currentName} you were working on. You will have to start on a new ${newName}.`;
	} else if (messageType === "socketCrash") {
		message =
			`Something went wrong with your connection to our server that could not be fixed.` +
			refreshSuffix;
	} else if (messageType === "requestPasswordResetFailure") {
		//These are from resets from parents for students.
		if (data.error === "E_PERMISSION") {
			message = `Permission to reset this student's password is denied.`;
		} else if (data.error === "E_RECENT_RESET_REQUEST") {
			message = `An email with instructions to reset this student's password has recently been sent. Please follow the instructions in that email.`;
		} else {
			message = `There was an issue resetting this student's password. Please try again.`;
		}
	} else if (messageType === "updateTempPasswordFailed") {
		//TODO: This should actually depend on the error from the backend, not just be a generic error.
		message = `Unable to update temp password. Please try again.`;
	} else if (messageType === "trophyUnavailable") {
		message = `The trophy problem you tried to go to is unavailable for this lesson.`;
	} else if (messageType === "lockedBlock") {
		message = `This lesson you tried to go to is locked.`;
	} else if (messageType === "lockedChapter") {
		message = `This chapter you tried to go to is locked.`;
	} else if (messageType === "lockedGrade") {
		message = `This BA level you tried to go to is locked.`;
	} else if (messageType === "blockUnderConstruction") {
		message = `This lesson you tried to go to is under construction.`;
	} else if (messageType === "lockedLabBlock") {
		message = `This puzzle set you tried to go to is locked.`;
	} else if (messageType === "lockedLabSpace") {
		message = `The Puzzle Lab is locked.`;
	} else if (messageType === "labTypeUnderConstruction") {
		message = `This puzzle type you tried to go to is under construction.`;
	} else if (messageType === "labBlockUnderConstruction") {
		message = `This puzzle set you tried to go to is under construction.`;
	} else if (messageType === "blockRestricted") {
		message = `This lesson you tried to go to is restricted.`;
	} else if (messageType === "enteredLimitedStarBlock") {
		message =
			`Since you've played these problems recently, you can only earn 1 star right now.` +
			` Play now to earn your first star and come back again later to earn more!`;
		// + ` You'll be able to earn more ${moment().to(moment(data.limitExpires))}.`;
	} else if (messageType === "noTrainerPermission") {
		const userRoles = data.userRoles;
		// TODO: do we want to deal with other in-house roles?
		if (
			!_.intersection(userRoles, ["student", "teacher", "student deactivated"])
				.length
		) {
			message = `Your subscription to Beast Academy does not include playing.`;
		} else {
			message = `Your subscription to Beast Academy is not currently active.`;
		}
	} else if (messageType === "onlyTrainerPermission") {
		message = `Your account only has access to Beast Academy lessons.`;
	} else if (messageType === "emptyHistory") {
		message = `Once you've played this lesson, click on the star button to review your most recent work and lesson history.`;
	} else if (messageType === "inProgressHistory") {
		message = `Once you have completed this lesson, click on the star button to review your most recent work and lesson history.`;
	} else if (messageType === "windowWidth") {
		message = `Beast Academy Online is designed for screen widths of at least ${data.width} pixels.`;
	} else if (messageType === "testInProgress") {
		message = `Welcome back!\n\nYou are currently taking the test for BA Level ${data.baLevel} Chapter ${data.chapter}: ${data.chapterName}.\n\nAll other areas of BA Online are locked until you submit your test.`;
	} else if (messageType === "testCooldown") {
		const expMoment = moment(data.expiration);
		const unlockTime = expMoment.format("h:mm a");
		const daysLeft = expMoment.diff(moment(), "days", true);
		const {forTooltip, user} = data;
		let endText;
		if (daysLeft < 1 && expMoment.format("D") === moment().format("D")) {
			endText = "today";
		} else if (
			parseInt(expMoment.format("D")) ===
			parseInt(moment().format("D")) + 1
		) {
			endText = "tomorrow";
		} else {
			endText = `this ${expMoment.format("dddd")}`;
		}
		if (forTooltip) {
			message = `The test is locked until ${unlockTime} ${endText} to give you time to prepare for your next attempt.`;
		} else {
			const adultType = OpsUser.getAdultTypeForStudent(user);
			const unlockText =
				adultType === "parent"
					? ", or ask your parent to unlock it from their Report page"
					: "";
			message = `This test is temporarily locked to give you time to prepare after your recent attempt.\n\nYou will be able to try again after ${unlockTime} ${endText}${unlockText}.`;
		}
	} else if (messageType === "sessionNotPlaying") {
		message =
			`It looks like you're playing Beast Academy Online on another device. ` +
			`If you would like to play on this device, click the Refresh button below.`;
	} else if (messageType === "setAvatarBeastFail") {
		message = `Something went wrong with updating your avatar. Please try again later.`;
	} else if (messageType === "deleteAvatarBeastFail") {
		message = `Something went wrong with deleting your photo. Please try again later.`;
	} else if (messageType === "bookTestInProgress") {
		message =
			"Welcome back!\n\nYou are currently taking a test.\n\nAll other areas of BA Online are locked until you submit your test.";
	} else if (messageType === "problemLoadingBookTest") {
		message = "There was a problem loading the test.";
	} else if (messageType === "inBookTestWindowForOtherTest") {
		message = data.attemptingToAccessUnlockedBookTest
			? "This exam will unlock again tomorrow. "
			: "";
		message += `You are scheduled for a different exam today. Please use the link here on the homework page to enter the ${data.scheduledTest} Exam.`;
	} else if (messageType === "mustSignout") {
		message = "You must sign out to use this page.";
	}

	return message;
};

const forConfirmMessageType = function(messageType, data) {
	let buttonTypes = ["yes", "cancel"];
	let message;
	let title;
	if (messageType === "enterLimitedStarBlock") {
		message =
			`Since you have played these problems recently, you can only earn one star right now.` +
			` You will be able to earn three stars ${moment().to(
				moment(data.limitExpires)
			)}.` +
			` Do you want to start the lesson now with limited stars?`;
	} else if (messageType === "enterUnlimitedBlockWithLimitedResult") {
		message =
			`The progress you have on these problems from ${moment(
				data.lastPlayed
			).fromNow()}` +
			` is limited to only earn 1 star.` +
			` Do you want to continue that work or start over with the ability to earn 3 stars?`;
		buttonTypes = ["continue", "restart"];
	} else if (messageType === "startTest") {
		message = `Ready to begin the test for BA Level ${data.baLevel} Chapter ${data.chapter}: ${data.chapterName}?\n\nOnce you start the test, all other areas of BA Online will be locked until you submit your test.`;
		buttonTypes = ["starttest", "donotstarttest"];
	} else if (messageType === "startRepeatTest") {
		message = `Ready to begin the test for BA Level ${data.baLevel} Chapter ${data.chapter}: ${data.chapterName}?\n\nOnce you start the test, all other areas of BA Online will be locked until you submit your test.`;
		buttonTypes = ["starttest", "viewhistory"];
	} else if (messageType === "startBookTest") {
		message = `Ready to begin the test for book ${data.bookHash}?\n\nOnce you start the test, all other areas of BA Online will be locked until you submit your test.`;
		buttonTypes = ["starttest", "donotstarttest"];
	} else if (messageType === "alreadyLoggedIn") {
		message = `You're already signed in as ${data.displayName}`;
		if (data.username) {
			message += ` (${data.username})`;
		}
		message += ".";
		buttonTypes = ["keepplaying", "logout"];
	} else if (messageType === "manualLocking") {
		title = `Lock ${_.capitalize(data.itemType)}`;
		const skipArticle = data.itemType === "Puzzle Lab";
		message = `Are you sure? Locking ${skipArticle ? "" : "a"} ${
			data.itemType
		} will prevent this student from playing ${skipArticle ? "" : "this"} ${
			data.itemType
		} until you manually unlock it.`;
		buttonTypes = ["lock", "cancel"];
	} else if (messageType === "manualUnlockLabItemBeforeLabUnlocked") {
		const {itemType} = data;
		message = `The Puzzle Lab unlocks at XP Level 10. Your student will not have access to this ${itemType} until they reach XP Level 10 unless you unlock the Puzzle Lab.`;
		buttonTypes = ["unlock lab", "cancel"];
	} else if (messageType === "finishTestOrQuit") {
		message = `All other areas of BA Online are locked until you submit your test.\n\nTo exit, you must quit the test from the summary screen.`;
		buttonTypes = ["testsummary", "keepworking"];
	} else if (messageType === "finishBookTest") {
		message = `All other areas of BA Online are locked until you submit your test.`;
		buttonTypes = ["testsummary", "keepworking"];
	}
	return {message, buttonTypes, title};
};

// ChapterCompleteModal / StartTestModal copy.
const forChapterCompleteModal = function(data) {
	const title = data.isLastChapterInLevel
		? `Level ${data.baLevel} complete!`
		: `Chapter ${data.chapter} complete!`;
	let subtext;
	if (data.backToAllLessons) {
		subtext =
			"Great work finishing your chapter test. " +
			`What an accomplishment finishing this ${
				data.isLastChapterInLevel ? "level" : "chapter"
			}!`;
	} else if (data.isLastChapterInLevel) {
		if (data.nextGradeNumber != null) {
			subtext =
				"Great work finishing your chapter test.\n" +
				`You're ready to take on level ${data.nextGradeNumber}!`;
		} else {
			subtext =
				"Great work finishing your chapter test. " +
				"What an accomplishment finishing this level!";
		}
	} else {
		const nextChapter =
			data.nextChapterNumber != null
				? data.nextChapterNumber
				: data.chapter + 1;
		subtext =
			"Great work finishing your chapter test.\n" +
			`You're ready to take on chapter ${nextChapter}.`;
	}
	return {title, subtext};
};

const forAcademyGraduateModal = function() {
	return {
		title: "You're a Beast Academy graduate!",
		subtext:
			"Ready for the next step? Explore Art of Problem Solving's Prealgebra program.",
		certificateButtonLabel: "DOWNLOAD CERTIFICATE",
		nextStepButtonLabel: "SEE WHAT'S NEXT",
		prealgebraHref: Constants.WHATS_NEXT_AFTER_BEAST_ACADEMY_ARTICLE_URL,
		hallwayCapCelebrationAriaLabel: "Open graduation celebration",
	};
};

const forHomeworkAllCompleteModal = function() {
	return {
		title: "Nice! You're all set for class.",
	};
};

const forChapterTestStartModal = function(data) {
	let title;
	const subtext =
		"Give yourself some focus time! \nThe rest of BA locks while you test.";
	if (data.isFinalAcademyTest) {
		title = "It's time for the FINAL test";
	} else if (data.isLastChapterInLevel) {
		title = `It's time for the last test in Level ${data.baLevel}!`;
	} else {
		title = `It's time for the Chapter ${data.chapter} Test!`;
	}
	const buttonTypes = data.isRepeat
		? ["starttest", "viewhistory"]
		: ["starttest", "donotstarttest"];
	return {title, subtext, buttonTypes};
};

// doesNotExist is true if the user goes to a URL like 3/13/1 or 3/12/20 or 6/1/1.
const forReactPageError = function({
	noProblems,
	doesNotExist,
	initErrored,
	isReactCrash,
	problemType,
	inModal,
	outOfMemory,
	locked,
	unlocksAt,
} = {}) {
	const getDefault = () => {
		if (problemType === "trophy") {
			return standardErrorMessages.playtrophy;
		} else if (problemType === "history") {
			return standardErrorMessages.playhistory;
		}
		return standardErrorMessages.playblock;
	};
	if (outOfMemory) {
		return (
			"Uh oh! Your browser is low on memory. " +
			"Consider closing other tabs you have open and refreshing the page."
		);
	} else if (locked && unlocksAt) {
		const unlocksAtFmt = moment(unlocksAt).format("llll");
		return "This lesson is locked. It will unlock at " + unlocksAtFmt + ".";
	} else if (locked) {
		return "This lesson is locked. Please choose a different one to play.";
	} else if (noProblems) {
		return standardErrorMessages.noproblems;
	} else if (doesNotExist) {
		return standardErrorMessages["404"];
	} else if (initErrored) {
		return getDefault();
	} else if (isReactCrash) {
		if (problemType) {
			return getDefault();
		} else if (inModal) {
			return "Uh oh! Something's broken. We're on it! Close this modal to get back to the lesson.";
		} else {
			return "Uh oh! Something's broken. We're on it! Click the button below to get back to the home page.";
		}
	} else {
		return getDefault();
	}
};

const forReactOfficePageError = function({outOfMemory} = {}) {
	if (outOfMemory) {
		return (
			"Your browser is low on memory. " +
			"Consider closing other tabs you have open and refreshing the page."
		);
	}
	return "Sorry, we're having technical issues. Our staff has been automatically notified and will work to resolve the issue as soon as possible.";
};

//
// LOGIN AND REGISTRATION
//

const forRegistrationCodeError = function(code) {
	if (code === "E_CODE_MISSING") {
		return `You have not entered a registration code.`;
	} else if (code === "E_NO_CODE") {
		return `That registration code is invalid.`;
	} else if (code === "E_CODE_EXPIRED") {
		return `This registration code has expired.`;
	} else if (code === "E_CODE_USED") {
		return `This registration code has already been used.`;
	} else if (commonErrorTypes[code]) {
		return commonErrorTypes[code];
	}
	//Default returned for E_CODE_TYPE_MISMATCH, E_USER_ID_MISMATCH (no special handling)
	return `There was an issue processing your registration code in the server. Please try again.`;
};

const forUsernameGenerationError = function(code) {
	if (code === "E_NO_CODE") {
		return `The code you're using to register is invalid. Please try registering again`;
	} else if (code === "E_CODE_EXPIRED") {
		return `The code you're using to register has expired.`;
	} else if (code === "E_CODE_USED") {
		return `The code you're using to register has already been used.`;
	} else if (commonErrorTypes[code]) {
		return commonErrorTypes[code];
	}
	//Default returned for E_CODE_TYPE_MISMATCH, E_USER_ID_MISMATCH (no special handling)
	return `There was an error getting username options. Please refresh and try registering again.`;
};

const forRegisterNoUsername = function() {
	return `You have not selected a username.`;
};

const forRegisterRememberUsername = function() {
	return `You need it to sign in. Write it down so you don't forget!`;
};

const forRegisterNoAvatar = function() {
	return `You have not selected an avatar.`;
};

const forRegisterNoSettings = function() {
	return `You must choose from the options above.`;
};

const forRegisterNoSegment = function() {
	return `Please select one answer per row.`;
};

const forRegisterPasswordCheckError = function(code, info) {
	const isPlural = info && info.length > 1;
	if (code === "E_PASSWORD_REPEAT_MISMATCH") {
		return `The passwords you entered do not match.`;
	} else if (code === "E_PASSWORD_TOO_SHORT") {
		return `That password is too short. Your password should be at least 5 characters long.`;
	} else if (code === "E_DISPLAY_NAME_IN_PASSWORD") {
		const displayNamePieces = StringUtil.combineNames(info);
		return `${displayNamePieces} ${
			isPlural ? "are" : "is"
		} part of your display name, ${
			isPlural ? "they" : "it"
		} shouldn't appear in your password.`;
	} else if (code === "E_USERNAME_IN_PASSWORD") {
		const usernamePieces = StringUtil.combineNames(info);
		return `${usernamePieces} ${
			isPlural ? "are" : "is"
		} part of your username, ${
			isPlural ? "they" : "it"
		} shouldn't appear in your password.`;
	} else if (code === "E_PASSWORD_IS_USERNAME") {
		return `Type your password in both boxes, not your username.`;
	} else if (code === "E_COMMON_PASSWORD") {
		return `That password is too common. Please choose a different password.`;
	}
	// eslint-disable-next-line no-console
	console.warn("Unknown password check error", code, info);
	return "Error setting password";
};

const forUpdateUnregisteredUserError = function(code) {
	if (code === "E_REGISTRATION_CODE_UNSPECIFIED") {
		return `There was an error with your registration code. Please try registering again.`;
	} else if (code === "E_NO_CODE") {
		return `The code you're using to register is invalid. Please try registering again.`;
	} else if (code === "E_CODE_EXPIRED") {
		return `The code you're using to register has expired.`;
	} else if (code === "E_CODE_USED") {
		return `The code you're using to register has already been used.`;
	} else if (code === "E_USER_ID_MISMATCH") {
		return `The code you're using does not match the user being updated.`;
	} else if (code === "E_INVALID_USERNAME") {
		return `That is not a valid username choice. Please choose another username.`;
	} else if (code === "E_USERNAME_USED") {
		return `That username has already been selected. Please choose another username.`;
	} else if (code === "E_REGISTRATION_IN_PROGRESS") {
		return `This step has already been completed. Please try registering again.`;
	} else if (code === "E_COMMON_PASSWORD") {
		return `That password is too common. Please choose a different password.`;
	} else if (commonErrorTypes[code]) {
		return commonErrorTypes[code];
	} else {
		return `There was an error with your registration code. Please try registering again.`;
	}
};

const forLoginError = ElevShared.copyForLoginError;

const forAccountSetupError = function(code, type) {
	if (code === "E_USERNAME_NOT_FOUND") {
		if (type === "parent") {
			return "This link is not valid. Please check that the purchase order was purchased with the correct email.";
		} else {
			return "This link is not valid. Please check that the account was set up with the correct email.";
		}
	} else if (code === "E_INVALID_SETUP_CODE") {
		if (type === "parent") {
			return "This link is not valid. This parent account may have already been set up.";
		} else {
			return "This link is not valid. This account may have already been set up.";
		}
	} else if (commonErrorTypes[code]) {
		return commonErrorTypes[code];
	} else {
		return "There was an issue setting up this account. Please try again later.";
	}
};

//
// INSTRUCTIONS
//

const forTrophyInstructions = function(enableContinuousPlay) {
	const unlockSentence = enableContinuousPlay
		? "A trophy problem is an extra challenge you can earn by getting at least 1 star on a lesson that has one."
		: "A trophy problem is an extra challenge you can earn by getting 3 stars on a lesson that has one.";
	return `This is a trophy problem. \n
	${unlockSentence}\n
	You have unlimited attempts, so do your best and keep trying if you can't solve it right away!\n\n`;
};

const forTestInstructions = function(names) {
	return `Please welcome our four competitors to the stage: ${names[0]}, ${names[1]}, ${names[2]}, ${names[3]}. \n\n`;
};

//
// BLOCKS
//

const forStairLocked = function(isUpstairs) {
	return `This stair is locked.\nEarn at least one star on the previous ${
		isUpstairs ? "downstairs " : ""
	}lesson to unlock it.`;
};

const forChapterLocked = function(chapter, user, navType) {
	const chapterModel = chapter.model;
	const {chapterIndex} = chapterModel;
	const chapterNumber = chapterIndex + 1;
	const adultType = OpsUser.getAdultTypeForStudent(user);
	if (!chapterModel.gradeUnlocked || !chapterModel.unlocked) {
		if (chapterModel.lockType === "override") {
			return `Your ${adultType} has locked this chapter.`;
		} else {
			let text = `Chapter ${chapterNumber} is locked.`;
			if (navType === "next") {
				// if this is for the previous chapter, don't talk about stars on this test
				text +=
					"\nEarn at least one star on this chapter's test to unlock this chapter.";
			}
			return text;
		}
	}
};

//Function shared by BlockButton and AuxBlockButton to generate a message about why a block is locked.
const forBlockLocked = function(block, chapter, user, forTooltip, demo) {
	if (demo) {
		return "";
	}
	const blockModel = block.model;
	const chapterModel = chapter.model;
	const {chapterIndex} = chapterModel;
	const chapterNumber = chapterIndex + 1;
	const adultType = OpsUser.getAdultTypeForStudent(user);
	if (!chapterModel.gradeUnlocked || !chapterModel.unlocked) {
		if (chapterModel.lockType === "override") {
			return `Your ${adultType} has locked this chapter.`;
		} else {
			return `Chapter ${chapterNumber} is locked.\nEarn at least one star on the previous chapter's test to unlock this chapter.`;
		}
	} else if (blockModel.lockOverride === "locked") {
		const blockName =
			blockModel.blockType === "test"
				? "the test for chapter " + chapterNumber
				: blockModel.displayName;
		return `Your ${adultType} has locked ` + blockName + `.`;
		// } else if (blockModel.restricted) {
		// 	const problemWord =
		// 		blockModel.restrictionData.reviewProblemsLeft === 1
		// 			? "problem"
		// 			: "problems";
		// 	return `You must answer ${blockModel.restrictionData.reviewProblemsLeft} more review ${problemWord} correctly before the test is unlocked again.`;
	} else if (
		blockModel.blockType === "test" &&
		OpsBlock.isInTestCooldown(block)
	) {
		return forErrorMessageType("testCooldown", {
			expiration: blockModel.testCooldownExpiration,
			reason: blockModel.testCooldownReason,
			forTooltip,
			user,
		});
	} else if (!blockModel.unlocked) {
		const blockType = blockModel.blockType;
		//Check if it’s a special block, if so, let the student know what they need to do to unlock it
		// if (blockType === "review") {
		// 	// For reference when later switching review back on: [T1723]
		// 	return `You need at least one star on ${numBlocks -
		// 		4} lessons in this chapter to unlock Review.`;
		// } else if (blockType === "challenge") {
		// 	// For reference when later switching review back on: [T1723]
		// 	// return `You need at least one star on the Chapter Test or at least one star on all 15 lessons in the chapter before you can unlock Challenge.`;
		// 	return `You need at least one star on ${numBlocks -
		// 		4} lessons in this chapter to unlock Challenge.`;
		// }
		if (blockType === "test") {
			// check if user is a virtual campus user; within VC, teachers can't unlock tests for students
			if (OpsUser.isVCStudent(user)) {
				return `This test is locked.\nEarn one or more stars on ${
					chapter.blocksNeededForTest
				} more core (downstairs) lesson${(chapter.blocksNeededForTest = 1
					? ""
					: "s")} in this chapter to unlock the test. Or, ask your teacher to unlock it from their Reports page.`;
			} else {
				return `This test is locked.\nEarn one star on more core (downstairs) lessons in this chapter to unlock the test. Or, ask your ${adultType} to unlock it from their Reports page.`;
			}
		}
		const roomType = blockModel.isReading ? "reading" : "lesson";
		if (TrainerCore.isCoreBlock(blockModel)) {
			if (forTooltip)
				return `Locked:\nStar the previous lesson to unlock this ${roomType}.`;
			else
				return `This ${roomType} is locked. Complete the previous lesson in this chapter with at least one star to unlock this room.`;
		} else {
			if (forTooltip)
				return `Locked:\nStar the previous downstairs lessons in this chapter to unlock this ${roomType}.`;
			else
				return `This ${roomType} is locked. Complete the previous downstairs lessons in this chapter with at least one star to unlock the stairs and this ${roomType}.`;
		}
	} else if (blockModel.underConstruction) {
		return (
			blockModel.displayName +
			` is currently under construction. Come back soon!`
		);
	} else if (
		blockModel.blockType === "test" &&
		OpsUser.bookTestAssignedNow(user)
	) {
		return `You have an exam scheduled today. Please click "HW" on the top menu to access that exam. This chapter test will unlock later.`;
	}
};
const forLabBlockLocked = function(block, labType, user, forModal) {
	const blockModel = block.model;
	const typeModel = labType.model;
	const {spaceUnlocked} = typeModel;
	const adultType = OpsUser.getAdultTypeForStudent(user);
	if (blockModel.erroredResult) {
		return (
			blockModel.displayName +
			` is currently under construction. Come back soon!`
		);
	}
	if (!spaceUnlocked || !typeModel.unlocked) {
		return forLabTypeLockStatus(
			blockModel.lockType || typeModel.lockType,
			adultType,
			labType.model
		);
	} else if (
		blockModel.lockOverride === "locked" ||
		blockModel.lockType === "override"
	) {
		return `Your ${adultType} has locked ` + blockModel.displayName + `.`;
	} else if (!blockModel.unlocked) {
		//Otherwise return a generic message
		if (forModal) {
			return `This puzzle set is locked. Play more unlocked ${typeModel.displayName} puzzles to unlock it.`;
		} else {
			return `This puzzle set is locked. Play more unlocked ${typeModel.displayName} puzzles to unlock it.`;
		}
	} else if (blockModel.underConstruction) {
		return (
			blockModel.displayName +
			` is currently under construction. Come back soon!`
		);
	}
};

/**
 * Returns a string describing why the lab is locked, prioritizing telling the
 * student about parent and teacher override locks before general Puzzle Lab
 * locks by level.
 *
 * @param {string} lockType The lock type of the lab block, ex. "override"
 * @param {string} adultType The adult type of the student, ex. "parent"
 * @param {Object} labType The lab type object, ex. {spaceUnlocked: true, spaceLockType: "override", requiredChapterID: 1, unlockLevel: 0}
 */
const forLabTypeLockStatus = function(lockType, adultType, labType) {
	const {
		spaceUnlocked,
		spaceLockType,
		requiredChapterID,
		unlockLevel,
		unlocked,
	} = labType;
	if (!spaceUnlocked) {
		if (spaceLockType === "override") {
			if (unlocked) {
				return `Your ${adultType} has unlocked the Puzzle Lab.`;
			} else {
				return `Your ${adultType} has locked the Puzzle Lab.`;
			}
		} else {
			const spaceUnlockLevel = UnlockUtil.getUnlockLevel("spaces", "lab");
			return `To unlock Puzzle Lab, you must reach XP Level ${spaceUnlockLevel}. Play lessons to earn XP and level up to unlock Puzzle Lab.`;
		}
	}
	if (lockType === "override") {
		if (unlocked) {
			return `Your ${adultType} has unlocked this puzzle type.`;
		} else {
			return `Your ${adultType} has locked this puzzle type.`;
		}
	} else {
		const displayName =
			labType.displayName || OpsLab.getTypeNameFromID(labType.typeID);
		if (requiredChapterID) {
			const context = GradeChapter.chapterIDToContext(requiredChapterID);
			return `${displayName} puzzles unlock with progress on BA${
				context.gradeNumber
			} Chapter ${context.chapterIndex + 1}.`;
		} else if (unlockLevel) {
			return `${displayName} puzzles unlock at XP Level ${unlockLevel}. Play lessons to earn XP and level up to unlock these puzzles.`;
		}
	}
};

/**
 * Returns a shorter version of forLabTypeLockStatus() for a lab while assuming that
 * the lab is locked.
 *
 * @param {string} lockType The lock type of the lab block, ex. "override"
 * @param {string} adultType The adult type of the student, ex. "parent"
 * @param {Object} labType The lab type object, ex. {spaceUnlocked: true, spaceLockType: "override", requiredChapterID: 1, unlockLevel: 0}
 */
const forLabTypeLockStatusShort = function(lockType, adultType, labType) {
	const {
		spaceUnlocked,
		spaceLockType,
		requiredChapterID,
		unlockLevel,
		unlocked,
	} = labType;
	if (!spaceUnlocked) {
		if (spaceLockType === "override") {
			if (unlocked) {
				return `Your ${adultType} has unlocked the Puzzle Lab`;
			} else {
				return `Your ${adultType} has locked the Puzzle Lab`;
			}
		} else {
			const spaceUnlockLevel = UnlockUtil.getUnlockLevel("spaces", "lab");
			return `Puzzle Lab unlocks at XP Level ${spaceUnlockLevel}`;
		}
	} else if (lockType === "override") {
		if (unlocked) {
			return `Your ${adultType} has unlocked this puzzle type`;
		} else {
			return `Your ${adultType} has locked this puzzle type`;
		}
	} else {
		if (requiredChapterID) {
			const context = GradeChapter.chapterIDToContext(requiredChapterID);
			return `Earning stars on\nBA${
				context.gradeNumber
			} CH${context.chapterIndex + 1}`;
		} else if (unlockLevel) {
			return `At XP Level ${unlockLevel}`;
		} else {
			// should never be here, as all labs should have unlock requirements
			return "";
		}
	}
};

const forLabInDemo = function() {
	return "The full version of BA Online includes a Puzzle Lab with more than 250 puzzles that complement the standard lessons.";
};

const forBookTestLocked = function(bookHash, lockedUntil) {
	const unlockTimeFormatted = moment.format(lockedUntil, "LLL");
	return `The test for unit ${bookHash} is locked until ${unlockTimeFormatted} or your teacher unlocks it.`;
};

const forUnfinishedBlocks = function(numUnfinished) {
	return (
		`You are in the middle of ${numUnfinished} other lessons in this chapter. ` +
		`Are you sure you want to start a new lesson?`
	);
};

const forSpaceProgressLocked = function(space) {
	let message = "";
	if (space === "lab") {
		message = "The Puzzle Lab unlocks at XP Level 10!";
	} else if (space === "library") {
		message = "Read any book section in Class building to unlock the library.";
	} else if (space === "theater") {
		message = "Watch any video in Class building to unlock the theater.";
	}

	return message;
};

//
// LIMITED STARS
//

//Text added to the bottom of the star info modal explaining why maxStars are 1.
const forExplainingLimitedMaxStars = function() {
	return (
		"Since you've played these problems recently, you can only earn 1 star right now." +
		" Play again later to earn 2 or 3 stars!"
	);
};

//Text for the modal checking if the user wants to play a block with maxStars 1
//that has already been starred
const forEnterLimitedStarBlock = function() {
	return (
		"Since you've played these problems recently, you can only earn 1 star right now." +
		" Are you sure you want to work on this lesson?"
	);
};

const forNoViewableHistoryResult = function() {
	return (
		"Oh no! There were some issues with the most recent set of problems you worked on." +
		" That work cannot be reviewed."
	);
};

//
// RESULT FLOW
//

const forInterventionSlider = function(resultStyleType) {
	const interventionMessages = {
		rote:
			"At ease, polliwog! \n\nBefore you restart this lesson, make sure you understand the mistakes you made.",
		kraken:
			"Arrrr...You're off to a rough start! \n\nI'll give you a chance to restart this lesson, but first be sure you know what you're doin'.",
		q:
			"Uh oh, having trouble? \n\nBefore you restart this lesson, can you figure out what you did wrong?",
		grok:
			"Looks like you could use some help. \n\nMake sure you understand your mistakes before you restart this lesson.",
		fiona:
			"Let's take a quick break. \n\nMake sure you understand your mistakes before you restart this lesson.",
		test:
			"Competitor, it looks like you could use some help. \n\nBefore you restart the chapter test, make sure to review this chapter.",
	};
	return interventionMessages[resultStyleType];
};

const forStarInfoInitialMessage = function(resultType) {
	const starInfoMessages = {
		review:
			"Each time you review, you build up a streak by getting problems right. But be careful! Every problem wrong cuts your streak in half.",
		puzzle: "In this lesson, you collect each star as soon as you earn it.",
	};
	return starInfoMessages[resultType];
};

const forRBTIncorrectFinalTriangle = function() {
	return `Click the  ((U))  or  ((R))  button below the grid to try again.`;
};

// doneRetrying should be a return of OpsResult.doneRetryingUpdates. Not
// including OpsResult here for fear of a later circular dependency.
// This must return falsy if the result is not actually failing.
const forFailingResult = function(
	result,
	{doneRetrying = false, disconnected = false} = {}
) {
	if (!result) {
		return null;
	} else if (doneRetrying) {
		return (
			`Something is wrong with these problems. ` +
			`Our staff has been notified, and we will fix it soon. ` +
			`You might need to wait a few days before trying this lesson again.`
		);
	} else if (disconnected) {
		return (
			`Your connection to our server has been lost. Check your internet connection. ` +
			`You will be able to continue play as soon as the connection is reestablished.`
		);
	} else if (result.lastUpdateFailed) {
		return (
			`We're having trouble saving your progress. ` +
			`Please wait while we retry.`
		);
	} else {
		return null;
	}
};

const forClearProblem = function() {
	return `Are you sure you want to clear all of your work on this problem?`;
};

const forLeaveResult = function({canResume, savesFailing, resultType}) {
	let message = `Are you sure you want to leave this lesson before it's complete?`;

	if (resultType && resultType === "review") {
		message = `Are you sure you want to leave Review now?
                You will keep any stars you have earned,
                but you'll have to start up a new streak next time.`;
	} else if (!canResume) {
		message += ` You can keep any stars you've earned, but will lose all of your current progress.`;
	} else if (savesFailing) {
		message += ` Your recent progress has not saved and may be lost.`;
	} else {
		message += ` You will be able to resume these problems later.`;
	}

	return message;
};

//
// SUBMITTING
//

const forSubmitSliderResponse = function(isCorrect, styleType) {
	let response = submitResponse.general;
	if (styleType && submitResponse[styleType]) {
		response = submitResponse[styleType];
	}
	if (isCorrect) {
		// Note: currently never used
		return response.correct;
	} else {
		// Trial messages go here?
		return response.incorrect;
	}
};

const forExampleSubmitSliderResponse = function(isCorrect, styleType) {
	return forSubmitSliderResponse(isCorrect, styleType);
};

const forSubmitGiveUp = function(canGiveUp, styleType) {
	let response = giveUpResponse.general;
	if (styleType && giveUpResponse[styleType]) {
		response = giveUpResponse[styleType];
	}
	if (canGiveUp) {
		return response.canGiveUp;
	} else {
		return response.noGiveUp;
	}
};

const forSubmitRepeat = function(styleType) {
	let response = repeatResponse.general;
	if (styleType && repeatResponse[styleType]) {
		response = repeatResponse[styleType];
	}
	return response;
};

const forSubmitTooSoon = function(styleType) {
	let response = submitTooSoonResponse.general;
	if (styleType && submitTooSoonResponse[styleType]) {
		response = submitTooSoonResponse[styleType];
	}
	return response;
};

const forSubmitMissingMessage = function() {
	// If this is used, ErrorUtil already told us a renderer message was missing.
	// We just need to save as much face as possible to the end-user.
	return `That answer could not be submitted. Take another look.`;
};

const forSessionExpired = function() {
	return `Your session has expired. You need to sign back in.`;
};

//For specific engines:

const forBubbleGameFinalBubbles = function(numStars) {
	//Each message must be an array of 3 strings to go in the 3 bubbles blown.
	const bubbleMessages = {
		0: ["Keep", "trying", "!"],
		1: ["Good", "job", "!"],
		2: ["Great", "popping", "!"],
		3: ["Awesome", "work", "!"],
	};
	return bubbleMessages[numStars];
};

const forHundredsChartCheckable = function(someIncorrect, divisor) {
	const div = divisor || "n";
	if (!someIncorrect) {
		return `You are missing some multiples of $${div}$. Keep trying! You'll be able to give up later by clicking this button.`;
	} else {
		return `Some of the marked numbers are not multiples of $${div}$. Keep trying! You'll be able to give up later by clicking this button.`;
	}
};

const forCheckTest = function() {
	return `Make sure to double-check your work before you submit.`;
};

const forReviewStreakDrop = function() {
	return `Your streak dropped, but your stars are still there! Keep playing to get more stars or leave the lesson and continue later.`;
};

const forReviewQuitMessage = function(data) {
	if (data.forForcedQuit) {
		// The message that appears when we kick them out.
		return `It's closing time. Come back and review again later!`;
	} else {
		//The message that appears giving them the option to quit
		return (
			`It's almost closing time. You need to get ${data.numProblems} right in a row to earn` +
			` your next star. Do you want to keep playing or quit and review again later?`
		);
	}
};

const forTypingX = function() {
	return `To get the symbol $*$ instead of the letter x, type * with Shift+8.`;
};

const forHiddenMessageRepeat = function() {
	return "You entered in the same answer for more than one letter.";
};

const forDemoEmailModal = function() {
	return {
		enterEmail:
			"Enter your email to receive updates on new features and content from Beast Academy.",
		pickRole: "Which role best describes you?",
		signingUp: "We are signing you up...",
		signUpSuccess: "Thanks for signing up!",
		signUpFail: "There was an issue signing you up.",
		duplicateEmail: "You are already signed up for our mailing list.",
		//Agreement checkbox
		agreement:
			`I confirm that I am 18 years old or older, and I consent to receive Beast Academy communications,` +
			` including product updates, announcements, and other outreach emails. All such emails include a link that` +
			` can be used to unsubscribe from these emails at any time.`,
		//Errors
		roleNotChosen: "* You must choose a role.",
		agreeNotChecked: "* You must agree to the terms.",
		emailMissing: "* Email address empty",
		emailInvalid: "* Email address invalid",
	};
};

const forDemoTutorialModal = function() {
	// For the other demo tutorial types, check in DemoTutorialModal.jsx since they are more complex.
	return {
		home1stVisit:
			"Welcome to Beast Academy Online! This tutorial will guide you through some important features. Let's start in the classroom, where BA students spend most of their time. Click the Class button to get started.",
		trainerChapter:
			"Welcome to Class! BA Online includes over 1,000 lessons arranged by level and chapter. Twenty of these lessons are available in the Demo. Explore by dragging around. Click any lesson to play.",
		libraryGrade:
			"Welcome to the Library. BA Online includes access to all of the Beast Academy Guide books. Here, we include sample sections from 20 different Guide chapters. Click any book section to read it.",
		libraryChapter:
			"This is just one section of the Beast Academy Guide book. With a subscription, students can read all 20 books! Once you're done reading, click the book icon to return to the Library.",
		theaterChapter:
			"The BA Online Theater includes over 1,000 instructional videos featuring Art of Problem Solving founder, author, and USA Mathematical Olympiad winner, Richard Rusczyk. It's like having a world-class tutor for your child.",
		profile:
			"This is the student Profile page. We've filled it with some fake data. Parents can view even more information on their Parent account. Click the Progress or Activity tab to see what's there.",
		sliderOpened:
			"This slider normally shows you all the content available for a given chapter. The icons in yellow indicate things you've completed, while those in grey are items you haven't.",
		replayLesson:
			"With a subscription, every lesson has at least 3 different sets of problems. So, students can replay any lesson with a new problem set. In the Demo version, every lesson has just one set of problems.",
		sellModal: {
			line1: "ENJOYING BEAST ACADEMY ONLINE?",
			line2: "ENROLL TODAY!",
			line3: "CANCEL WITHIN 14 DAYS FOR A FULL REFUND.",
			subscriptionButton: "SUBSCRIPTION OPTIONS",
			keepPlayingButton: "KEEP PLAYING",
			emailSignUpButton: "JOIN OUR EMAIL LIST",
		},
		trophy:
			"This is a trophy problem. In BA Online, students earn access to the trophy problem by 3-starring the lesson. Students have unlimited attempts to solve these extra-challenging problems.",
	};
};

const forDemoTestModal = function() {
	return "All 60 chapters of Beast Academy Online include cumulative tests so students can evaluate their skills before moving on.";
};

const forBugReport = function() {
	return {
		success: `Thank you for submitting a bug report. We review all bug reports and make changes as necessary, but cannot reply to bug report messages.`,
		dragArrow: `Drag the arrow to point out the issue. Then, click submit.`,
		submittedBug: `Successfully submitted!`,
		submittingBug: `Submitting bug report...`,
		repeatSubmitProblem: `You have already submitted a bug report for this problem. Is there more information you would like to give us?`,
		blockSubmitBug: `We have received your reports and will look into it. You can ask your parent or teacher if you have any further questions about it.`,
		onBeforeUnloadMessage: `You're currently submitting a bug report. Are you sure you want to cancel it?`,
	};
};

const getBugReportOptions = function(pageName, readAloudShown) {
	// problem goes to curriculum team, site goes to devs, neverland gets no-actioned
	const options = {
		"My answer is right": "neverland",
		"There is a mistake in the problem": "problem",
		"Beast Academy isn't working correctly": "site",
		"Something is missing": "site",
		"The problem is read incorrectly": "problem",
	};
	if (pageName === "all") {
		return options;
	} else if (pageName === "trainer.problem") {
		const pickList = [
			"My answer is right",
			"There is a mistake in the problem",
			"Beast Academy isn't working correctly",
		];
		if (readAloudShown) pickList.push("The problem is read incorrectly");
		return _.pick(options, pickList);
	} else if (pageName === "lab.problem") {
		const pickList = [
			"My answer is right",
			"There is a mistake in the problem",
			"Beast Academy isn't working correctly",
		];
		if (readAloudShown) pickList.push("The problem is read incorrectly");
		return _.pick(options, pickList);
	} else {
		// Any other page
		// Left in the "Something is missing" choice from options so that these pages have two options.
		return _.pick(options, [
			"Beast Academy isn't working correctly",
			"Something is missing",
		]);
	}
};

const forSummaryStars = function(
	numStars,
	{
		forIncompleteResult = false,
		forTest = false,
		forBookTest = false,
		forUnlockedTrophy = false,
		forUnlockedEnrichmentLesson = false,
	} = {}
) {
	if (forBookTest) return;
	if (forTest) {
		if (numStars === 0) {
			return "Review before you try again.";
		} else if (numStars === 1) {
			return `You passed your test and earned 1 star!`;
		} else {
			return `You passed your test and earned ${numStars} stars!`;
		}
	}
	if (forUnlockedTrophy) {
		return "Trophy problem unlocked!";
	}
	if (forUnlockedEnrichmentLesson) {
		return "Beast lesson unlocked!";
	}
	if (numStars === 0 && !forIncompleteResult) {
		return "Review before you try again.";
	}
	const intro = forIncompleteResult
		? "So far, you have earned "
		: "You earned ";
	return intro + StringUtil.getPluralString(numStars, "star") + "!";
};

const forSummaryReplay = function(numStars) {
	return numStars === 0 ? "Review then replay:" : null;
};

const forSummaryNext = function() {
	return "Next:";
};

const forBookTestSummary = function(problemsSolvedText) {
	return (
		problemsSolvedText +
		" Click the question numbers above to review the solutions."
	);
};

const forManyMissedProblems = function(numProblemsMissed) {
	return numProblemsMissed <= 5
		? "Review these problems you missed:"
		: "Review the problems you missed by clicking on the red problem circles above.";
};

const forSummaryLabTop = function(celestialID) {
	const celestialData = LabUtil.getCelestialFromID(celestialID);
	const celestialName = celestialData.displayName.toLowerCase();
	return `You earned a ${celestialName}!`;
};

const forSummaryLabBottom = function(numProblems) {
	return (
		"You solved " +
		(numProblems > 1 ? `all ${numProblems} puzzles.` : "the puzzle.")
	);
};

const forTestTrialMessages = function() {
	return `The answer for this problem was invalid and could not be checked.`;
};

const forHomeworkTab = function() {
	return {
		notAvailable: `Homework will be available the first week of class.`,
	};
};

const reqOptNounClauses = function(numRequired, numOptional) {
	const nounClauses = [];

	const formatNounClause = function(numLessons, type) {
		if (numLessons === 0) return;
		nounClauses.push(
			numLessons + ` ${type} lesson${numLessons > 1 ? "s" : ""}`
		);
	};

	formatNounClause(numRequired, "required");
	formatNounClause(numOptional, "mastery");

	return nounClauses;
};

const forHomeworkWeekInstructions = function({
	numRequired,
	numOptional,
	dueDate,
	omitIfNoneAssigned = false,
}) {
	if (numRequired === 0 && numOptional === 0) {
		if (omitIfNoneAssigned) return null;
		return `No lessons are assigned this week.`;
	}

	const nounClauses = reqOptNounClauses(numRequired, numOptional);

	let byDateStr = "";

	if (dueDate) {
		byDateStr = " by " + dueDate.format("MMMM Do");
	}

	return `You must complete ${nounClauses.join(" and ")}${byDateStr}.`;
};

const forHomeworkProgressBarTooltip = function(
	numRequired,
	numOptional,
	examHash
) {
	if (examHash)
		return `This progress bar shows your performance on the ${examHash} exam.`;
	let reqPhrase = ``;
	let optPhrase = ``;
	const pluralizeLesson = x => (x > 1 ? `lessons` : `lesson`);

	if (numRequired > 0) {
		reqPhrase = ` the ${numRequired} required ${pluralizeLesson(numRequired)}`;
	}

	if (numOptional > 0) {
		optPhrase = ` your ${
			numOptional === 1 ? `single` : numOptional
		} best mastery ${pluralizeLesson(numOptional)}`;
	}

	const andPhrase = reqPhrase && optPhrase ? ` and` : ``;

	return `This progress bar shows your performance on${reqPhrase}${andPhrase}${optPhrase}.`;
};

const forHomeworkDueDateTooltip = function(dueDate) {
	const isWas = dueDate.diff(moment()) > 0 ? "is" : "was";

	return `This homework ${isWas} due on ${dueDate.format("MMMM Do")}`;
};

const forHomeworkIconTooltip = function(numCompleted, numTotal, isRequired) {
	const reqOpt = isRequired ? "required" : "mastery";

	return `You have completed ${numCompleted} out of ${numTotal} ${reqOpt} lessons.`;
};

const forBlockHomeworkIconTooltip = function(isCurrentWeek) {
	const currentPrev = isCurrentWeek ? "" : " for a previous meeting";
	return `This is a homework lesson${currentPrev}.`;
};

const forHomeworkReadings = function(readings) {
	return `Readings: ${readings}.`;
};

const forSwitchToStudent = function(isParent) {
	let message = `To play BA Online, switch to a BA Student Account.`;
	if (isParent)
		message += `\nOr, click Reports to go to your Parent Reports pages.`;
	else message += `\nOr, click Enroll to subscribe to BA Online.`;
	return message;
};

const forDeactivatedTeacher = function() {
	return "Your account currently does not have access to play Beast Academy Online.";
};

const forUserRateLimitModal = function(initialSeconds) {
	const secondsString = StringUtil.getPluralString(initialSeconds, "second");
	return (
		`Uh oh! Something went wrong. ` +
		`Please wait ${secondsString} before refreshing.`
	);
};

const forTheaterToBlockModal = function(adultType) {
	return {
		noStarMessage:
			"Do you want to leave the theater and play the lesson that goes with this video?",
		starredMessage:
			"You have already starred the lesson that goes with this video. Would you like to play it again?",
		starredDemoMessage:
			"You have already played the lesson that goes with this video. With a subscription, every lesson has at least 3 different sets of problems. So, students can replay any lesson with a new problem set. In the Demo version, every lesson has just one set of problems. Would you like to play it again?",
		autoLockedMessage:
			"You have not unlocked the lesson that goes with this video.",
		manuallyLockedMessage:
			"Your " + adultType + " has locked the lesson that goes with this video.",
	};
};

const forLibraryToBlockModal = function(messageType, user) {
	const adultType = OpsUser.getAdultTypeForStudent(user);
	if (messageType === "autoLocked") {
		return "You have not unlocked this lesson.";
	} else {
		return `Your ${adultType} has locked this lesson.`;
	}
};

const forActivityCalendar = function() {
	return "Numbers shown are minutes played.";
};

const forLevelProgressBar = function(remainingXP, currLevel, nextLevel) {
	return `You're at XP Level ${currLevel} and need ${remainingXP} more XP to reach XP Level ${nextLevel}. Complete lessons to earn more XP.`;
};

const getLastLessonMessage = function(chapter, forDemo = false) {
	if (forDemo) {
		return `This is the last lesson in the demo for Level ${chapter.model.gradeNumber}!`;
	} else {
		return `This is the last lesson in Chapter ${chapter.model.chapterIndex +
			1}!`;
	}
};

const getLastLessonInDemoGradeMessage = function(chapter) {
	return `This is the last lesson in the demo for Level ${chapter.model.gradeNumber}!`;
};

const getEmptyLeaderboardsMessage = function() {
	return "No one is on the leader board yet. You could be the first!";
};

const getChapterSliderMessage = function(info = {}) {
	const {type} = info;
	if (!type) return "";
	if (type === "unlockByTimesCompleted") {
		return `Need a break from ${info.blockName}? We’ve unlocked the next lesson.`;
	} else {
		return "";
	}
};

const profileTeacherDescriptions = {
	q: "Word Problems",
	grok: "Puzzles",
	fiona: "Math Team",
	kraken: "General",
	rote: "Skill Drill",
	ms_levans: "Challenges",
};

const tooltipText = {
	homeIcon: "Home",
	baHomeIcon: "Home",
	beamIcon: "BEAM Class",
	projectorIcon: "Theater",
	bellIcon: "Class",
	settingsIcon: "Settings",
	leaderboardsIcon: "Leaderboards",
	helpIcon: "Help",
	bookIcon: "Library",
	profileIcon: "Profile",
	bug: "Report a bug",
	lockedBlock: "This lesson is locked.",
	bonusBlock: "Enrichment",
	unfinishedBlock: "Play in progress.",
	chapterButton: "Chapter",
	labTypeButton: "Puzzle Type",
	gradeButton: "Browse chapters",
	gradeLibraryButton: "Browse books",
	gradeVideosButton: "Browse videos",
	trophy: "Trophy problem",
	info: "Instructions",
	pencil: "Draw",
	eraser: "Erase",
	undo: "Undo",
	redo: "Redo",
	reset: "Reset",
	revert: "Revert to last saved",
	photo: "Take avatar photo",
	trash: "Delete",
	palette: "Color",
	rotate: "Rotate",
	reflecth: "Flip",
	reflectv: "Flip",
	brush: "Color",
	resize: "Resize",
	layerTop: "Layer to top",
	layerBottom: "Layer to bottom",
	layerUp: "Layer up",
	layerDown: "Layer down",
	duplicate: "Duplicate",
	discard: "Discard",
	theaterBellButton: "Play the lesson",
	singleBookButton: "Go to book section",
	theaterProblemButton: "Watch video",
	homeworkIcon: "Homework",
	incompleteAssignmentIcon: "Assigned lesson",
	completeAssignmentIcon: "Completed assignment",
	incompleteTestIcon: "Assigned test",
	completeTestIcon: "Completed test",
	history: "History",
	videoPlay: "Play",
	videoPause: "Pause",
	videoReplay: "Replay",
	videoMute: "Mute",
	videoUnmute: "Unmute",
	videoCaptions: "Closed Captions",
	videoMakeFullScreen: "Expand",
	videoLeaveFullScreen: "Collapse",
	labTypes: "Browse puzzle types",

	// Chapter Path
	stairsUp: "Go upstairs",
	stairsDown: "Go downstairs",
	findMyAvatarIcon: "Find my avatar",
};

const altText = {
	trainerChapterButton: "Return to chapter path",
	trainerLibraryButton: "Go to book section",
	trainerTheaterButton: "Watch video",
	theaterTrainerButton: "Go to related lesson",
	labBlockReturn: "Return to puzzle type",
	labLobbyReturn: "Return to puzzle lobby",
	demoProblemReturn: "Return to chapter path",
	demoTheaterReturn: "Return to video select",
};

/*******
HARDCODED COPY:
*******/
// GlobalModal.jsx (page not found modal)

module.exports = {
	standardErrorMessages,
	forStandardError,
	get404Copy,
	getProtectedViewCopy,
	forGlobalErrorNotice,
	forErrorMessageType,
	forConfirmMessageType,
	forChapterCompleteModal,
	forAcademyGraduateModal,
	forHomeworkAllCompleteModal,
	forChapterTestStartModal,
	forReactPageError,
	forReactOfficePageError,
	forRegistrationCodeError,
	forUsernameGenerationError,
	forRegisterNoUsername,
	forRegisterRememberUsername,
	forRegisterNoAvatar,
	forRegisterNoSettings,
	forRegisterNoSegment,
	forRegisterPasswordCheckError,
	forUpdateUnregisteredUserError,
	forLoginError,
	passwordRuleCopy: ElevShared.passwordRuleCopy,
	forTrophyInstructions,
	forTestInstructions,
	forStairLocked,
	forChapterLocked,
	forBlockLocked,
	forLabBlockLocked,
	forLabTypeLockStatus,
	forLabTypeLockStatusShort,
	forSpaceProgressLocked,
	forLabInDemo,
	forBookTestLocked,
	forUnfinishedBlocks,
	forInterventionSlider,
	forStarInfoInitialMessage,
	forExplainingLimitedMaxStars,
	forEnterLimitedStarBlock,
	forNoViewableHistoryResult,
	forRBTIncorrectFinalTriangle,
	forFailingResult,
	forClearProblem,
	forLeaveResult,
	forSubmitSliderResponse,
	forExampleSubmitSliderResponse,
	forSubmitGiveUp,
	forSubmitRepeat,
	forSubmitTooSoon,
	forSubmitMissingMessage,
	forSessionExpired,
	forBubbleGameFinalBubbles,
	forHundredsChartCheckable,
	forCheckTest,
	forReviewStreakDrop,
	forReviewQuitMessage,
	forTypingX,
	forHiddenMessageRepeat,
	forDemoEmailModal,
	forDemoTutorialModal,
	forDemoTestModal,
	forBugReport,
	getBugReportOptions,
	forSummaryStars,
	forSummaryReplay,
	forSummaryNext,
	forBookTestSummary,
	forManyMissedProblems,
	forTestTrialMessages,
	forHomeworkTab,
	forHomeworkWeekInstructions,
	forHomeworkProgressBarTooltip,
	forHomeworkDueDateTooltip,
	forHomeworkIconTooltip,
	forBlockHomeworkIconTooltip,
	forHomeworkReadings,
	forSwitchToStudent,
	forDeactivatedTeacher,
	forActivityCalendar,
	tooltipText,
	altText,
	profileTeacherDescriptions,
	forAccountSetupError,
	forUserRateLimitModal,
	forTheaterToBlockModal,
	forLibraryToBlockModal,
	forSummaryLabTop,
	forSummaryLabBottom,
	forLevelProgressBar,
	getLastLessonMessage,
	getLastLessonInDemoGradeMessage,
	getEmptyLeaderboardsMessage,
	getChapterSliderMessage,
};
